import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  clientApiService,
  InvoiceDetail,
} from "../../services/client-api.service";
import { UploadReceiptModal } from "../../components/payments/UploadReceiptModal";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  partial: { label: "Pago parcial", className: "bg-blue-100 text-blue-700" },
  paid: { label: "Pagada", className: "bg-green-100 text-green-700" },
  overdue: { label: "Vencida", className: "bg-red-100 text-red-700" },
  sent: { label: "Enviada", className: "bg-slate-100 text-slate-700" },
  cancelled: { label: "Cancelada", className: "bg-slate-100 text-slate-400" },
};

const METHOD_LABELS: Record<string, string> = {
  cash: "Efectivo",
  transfer: "Transferencia",
  card: "Tarjeta",
  check: "Cheque",
  other: "Otro",
};

const ETL_URL =
  import.meta.env.VITE_API_URL_ETL || "https://worker-etl.betalog-ventas.workers.dev";

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(t: number | null) {
  if (!t) return "—";
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function getReceiptUrl(receiptId: string, fileUrl: string) {
  if (fileUrl.startsWith("http")) return fileUrl;
  return `${ETL_URL}/api/public/photos/receipts/${receiptId}`;
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientApiService.getInvoice(id);
      setDetail(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar factura");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
        {error || "Factura no encontrada"}
        <Link to="/dashboard/payments" className="ml-3 underline font-medium">
          ← Volver a pagos
        </Link>
      </div>
    );
  }

  const { invoice, items, payments, receipts } = detail;
  const status = STATUS_LABELS[invoice.status] || {
    label: invoice.status,
    className: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/payments"
        className="text-sm text-slate-500 hover:text-primary inline-flex items-center gap-1"
      >
        ← Volver a estado de cuenta
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
                {status.label}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              Factura {invoice.number}
            </h1>
            {invoice.projectId && (
              <Link
                to={`/dashboard/projects/${invoice.projectId}`}
                className="text-sm text-primary hover:underline font-medium mt-1 inline-block"
              >
                {invoice.projectName}
              </Link>
            )}
            <p className="text-sm text-slate-500 mt-2">
              📅 Emitida: {formatDate(invoice.dateIssued)} • Vence: {formatDate(invoice.dateDue)}
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            {invoice.status !== "paid" && invoice.status !== "cancelled" && (
              <button
                onClick={() => setShowUpload(true)}
                className="text-sm bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-medium transition"
              >
                📎 Subir comprobante
              </button>
            )}
          </div>
        </div>

        {/* Totales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-slate-100">
          <TotalBlock label="Subtotal" value={formatCurrency(invoice.subtotal)} />
          <TotalBlock label="IVA" value={formatCurrency(invoice.tax)} />
          <TotalBlock label="Total" value={formatCurrency(invoice.total)} bold />
          <TotalBlock
            label="Saldo pendiente"
            value={formatCurrency(invoice.pendingAmount)}
            highlight={invoice.pendingAmount > 0}
          />
        </div>
      </div>

      {/* Conceptos */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Conceptos</h2>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Sin conceptos detallados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-xs text-slate-500">
                <tr className="text-left">
                  <th className="py-2 pr-3">Descripción</th>
                  <th className="py-2 px-3 text-right">Cant.</th>
                  <th className="py-2 px-3">Unidad</th>
                  <th className="py-2 px-3 text-right">P. unitario</th>
                  <th className="py-2 pl-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="py-2 pr-3 text-slate-800">{it.description}</td>
                    <td className="py-2 px-3 text-right">{it.quantity}</td>
                    <td className="py-2 px-3 text-slate-500">{it.unit}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(it.unitPrice)}</td>
                    <td className="py-2 pl-3 text-right font-medium">
                      {formatCurrency(it.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Pagos aplicados */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">
            Pagos aplicados
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({payments.length})
            </span>
          </h2>
        </div>
        {payments.length === 0 ? (
          <p className="text-sm text-slate-500">Aún no hay pagos registrados.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {payments.map((p) => (
              <li key={p.id} className="py-3 flex items-center gap-3">
                <span className="text-xl">
                  {p.method === "cash" ? "💵" : p.method === "transfer" ? "🏦" : "💳"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">
                    {METHOD_LABELS[p.method] || p.method}
                    {p.reference && (
                      <span className="text-xs text-slate-400 font-normal ml-2">
                        Ref: {p.reference}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatDate(p.datePaid)}
                    {p.notes ? ` • ${p.notes}` : ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-green-700">{formatCurrency(p.amount)}</p>
                  {p.receiptPhotoUrl && (
                    <a
                      href={p.receiptPhotoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      Ver recibo
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Comprobantes subidos por el cliente */}
      {receipts.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Mis comprobantes subidos
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({receipts.length})
            </span>
          </h2>
          <ul className="space-y-3">
            {receipts.map((r) => {
              const statusBadge =
                r.status === "approved"
                  ? { label: "✅ Aprobado", className: "bg-green-100 text-green-700" }
                  : r.status === "rejected"
                  ? { label: "❌ Rechazado", className: "bg-red-100 text-red-700" }
                  : { label: "⏳ Pendiente", className: "bg-yellow-100 text-yellow-700" };
              return (
                <li
                  key={r.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <span className="text-xl">📎</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">
                      {r.amountDeclared ? formatCurrency(r.amountDeclared) : "Comprobante"}
                      {r.methodDeclared && ` • ${METHOD_LABELS[r.methodDeclared] || r.methodDeclared}`}
                    </p>
                    <p className="text-xs text-slate-400">
                      Subido: {formatDate(r.createdAt)}
                    </p>
                    {r.rejectionReason && (
                      <p className="text-xs text-red-600 mt-1">
                        Motivo: {r.rejectionReason}
                      </p>
                    )}
                  </div>
                  <a
                    href={getReceiptUrl(r.id, r.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    Ver
                  </a>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge.className}`}>
                    {statusBadge.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Modal subir comprobante */}
      {showUpload && (
        <UploadReceiptModal
          invoiceId={invoice.id}
          projectId={invoice.projectId}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function TotalBlock({
  label,
  value,
  bold = false,
  highlight = false,
}: {
  label: string;
  value: string;
  bold?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p
        className={`${bold ? "text-lg font-bold" : "text-base font-semibold"} ${
          highlight ? "text-primary" : "text-slate-900"
        }`}
      >
        {value}
      </p>
    </div>
  );
}